import classNames from 'classnames';
import { ChangeEvent, useEffect, useState } from 'react';

import { getLocationByZip } from '~components/ZipCodeForm/util';
import MarketoForm from '~globals/Marketo/marketoForm';
import { getAdvertiseId, isValidEmail, redirectToEhmp } from '~globals/common';
import { fireTrackEvent } from '~globals/tracking';
import { useAppDispatch, useAppSelector } from '~hooks/useRedux';
import { updateCounty, updateLead, updateZipAndCounty } from '~redux/Actions/GlobalActions';

import * as styles from './PartnerForm.module.css';
import { createMcwsLead } from './createMcwsLead';
import { formatPhoneInput } from './formatPhone';

type Props = {
    partnername?: string;
    mktoformid?: number;
};

const disclaimerText =
    'Your information and use of this site is governed by our most recent Terms of Use and Privacy Policy. By giving your information above and clicking the Submit Form button, you consent to receive calls, emails or text messages about Medicare Advantage, Medicare supplement insurance and prescription drug plan options (at any number or email address you provide) from eHealth or a licensed insurance agent partner using an autodialing system or artificial/prerecorded voice even if on a government do-not-call registry. Standard message and data rates apply. This agreement is not a condition of enrollment.';

const PartnerZipLeadComboForm = ({
    partnername = 'sutter',
}: // mktoformid: mktoFormId = 1004,
Props) => {
    1;
    const [id] = useState(() => `email-${Math.random().toString(36).substring(2, 9)}`);
    const [firstNameValue, setFirstNameValue] = useState('');
    const [lastNameValue, setLastNameValue] = useState('');
    const [emailValue, setEmailValue] = useState('');
    const [phoneValue, setPhoneValue] = useState('');
    const [consentValue, setConsentValue] = useState('' as 'Y' | 'N' | '');
    const [zipValue, setZipValue] = useState(
        useAppSelector((state) => state.global.geoLocation.zip)
    );
    const [countyList, setCountyList] = useState([] as string[]);

    const [isBusy, setIsBusy] = useState(false);
    const [isEmailValid, setIsEmailValid] = useState(true);
    const [isPhoneValid, setIsPhoneValid] = useState(true);
    const [isZipValid, setIsZipValid] = useState(true);

    const allid = useAppSelector((state) => state.global.allianceId);
    const gclid = useAppSelector((state) => state.global.gclid);
    const fbclid = useAppSelector((state) => state.global.fbclid);
    const msclkid = useAppSelector((state) => state.global.msclid);
    const sid = useAppSelector((state) => state.global.trackingParams.sid);
    const pid = useAppSelector((state) => state.global.trackingParams.pid);
    const serviceHour = useAppSelector((state) => state.global.hours);
    const servicePhone = useAppSelector((state) => state.global.phone.number);
    const countyName = useAppSelector((state) => state.global.geoLocation.countyName);
    const trackingParams = useAppSelector((state) => state.global.trackingParams);

    const dispatch = useAppDispatch();

    // const mktoForm = new MarketoForm(mktoFormId);

    const handlePhoneChange = (e: React.KeyboardEvent<HTMLInputElement>) => {
        formatPhoneInput(e);
        setPhoneValue((e.target as HTMLInputElement).value);
    };

    const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
        setIsEmailValid(true);
        setEmailValue(e.target.value);
    };

    const handleZipChange = (e: ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        if (value.match(/^[0-9]*$/)) {
            setZipValue(value);
        }
    };

    const handleSubmit = async () => {
        if (!validateForm() || isBusy) {
            return;
        }
        if (!emailValue && !phoneValue) {
            fireTrackEvent({
                page: { action: partnername + ': Submit Form' },
                leadInfo: { email: emailValue, phoneNumber: phoneValue },
            });
            redirectToEhmp({
                allid: allid,
                zip: zipValue,
                county: countyName,
                ...trackingParams,
                action: 'redirect_with_zip_code',
            });
            return;
        }

        setIsBusy(true);
        const leadRequest: LeadV1Request = {
            productType: 'MA',
            firstName: firstNameValue,
            lastName: lastNameValue,
            zip: zipValue,
            allid: allid,
            ...{ email: emailValue },
            ...{ phone: phoneValue },
            ...(sid && { sid }),
            ...(pid && { pid }),
            addScreenshot: true,
            subscribe: consentValue || 'N',
            disclaimerUrl: window.location.href,
            disclaimerText: disclaimerText,
            leadFormViewParam: {
                serviceHour,
                servicePhone,
                tcpaDisclaimers: disclaimerText,
                site: 'ehim',
                type: 'medicare-lp-sutter5',
            },
        };

        const advertise = getAdvertiseId(gclid, fbclid, msclkid) as { [key: string]: string };
        if (advertise) {
            leadRequest.advertiseIdType = advertise.id.toUpperCase();
            leadRequest.advertiseId = advertise[advertise.id];
        }

        try {
            const leadResp = await createMcwsLead(leadRequest);

            if (leadResp.data.createUpdateLeadMcwsApi?.success) {
                const { leadId, leadToken = '' } = leadResp.data.createUpdateLeadMcwsApi;
                dispatch(updateLead(String(leadId), leadToken));
                fireTrackEvent({
                    page: { action: partnername + ': Submit Form' },
                    leadInfo: { leadId, email: emailValue, phoneNumber: phoneValue },
                    events: ['save_results', 'leadCreated'],
                });

                setIsBusy(false);
                redirectToEhmp({
                    allid: allid,
                    zip: zipValue,
                    county: countyName,
                    ...(leadToken && { leadToken }),
                    ...trackingParams,
                    action: 'redirect_with_zip_code',
                });
            }
            setIsBusy(false);
        } catch (err) {
            console.error('Lead error:', err);
        }
    };

    const validateForm = () => {
        let firstError = null;
        if (!_validatePhone()) {
            firstError = `phone_${id}`;
        }
        if (!_validateEmail()) {
            firstError = firstError || `email_${id}`;
        }
        if (!_validateZip()) {
            firstError = firstError || `zip_${id}`;
        }
        if (firstError) {
            document.getElementById(firstError)?.focus();
            return false;
        }
        return true;
    };

    const _validateEmail = () => {
        const shouldSkip = emailValue.length === 0 && consentValue !== 'Y';
        if (shouldSkip || isValidEmail(emailValue)) {
            setIsEmailValid(true);
            return true;
        }
        setIsEmailValid(false);
        fireTrackEvent({
            form: {
                errors: 'error : email invalid',
            },
        });
        return false;
    };

    const _validatePhone = () => {
        if (phoneValue.length === 0 || new RegExp(/\d{3}-\d{3}-\d{4}/g).test(phoneValue)) {
            setIsPhoneValid(true);
            return true;
        }
        setIsPhoneValid(false);
        return false;
    };

    const _validateZip = () => {
        if (!isZipValid) {
            return false;
        }
        if (zipValue.length !== 5) {
            setIsZipValid(false);
            return false;
        }
        return true;
    };

    useEffect(() => {
        const getCountyList = async (zip: string) => {
            const response = await getLocationByZip(zip);
            if (
                !response?.data?.areaByZipCode?.locationList ||
                response.data.areaByZipCode.hasError
            ) {
                setIsZipValid(false);
                return;
            }
            const { locationList } = response.data.areaByZipCode;
            if (locationList.length === 1) {
                dispatch(updateCounty(locationList[0].countyName));
            } else {
                setCountyList(
                    response.data.areaByZipCode.locationList.map((location) => location.countyName)
                );
                // select the first county by default (to follow the behaviour present on the rest of the application)
                dispatch(updateCounty(locationList[0].countyName));
            }
        };
        setCountyList([]);
        setIsZipValid(true);
        if (zipValue.length === 5) {
            void getCountyList(zipValue);
        }
    }, [zipValue]);

    useEffect(() => {
        if (!emailValue) {
            setConsentValue('');
        }
    }, [emailValue]);

    return (
        <div
            className={classNames(
                'border-t-8',
                `border-branded-${partnername}`,
                'rounded-md shadow-md',
                'w-full max-w-screen-md',
                'flex flex-col items-center',
                'bg-surface-light'
            )}
        >
            <form
                className="max-w-[360px] p-4 flex flex-col gap-y-4 text-left"
                aria-live="assertive"
            >
                <div className="flex flex-col gap-3">
                    <label htmlFor="fname" className="text-16 md:text-18">
                        First Name
                    </label>
                    <input
                        id="fname"
                        className="rounded-sm border border-gray-disabled px-1 min-h-10"
                        value={firstNameValue}
                        onChange={(e) => setFirstNameValue(e.target.value)}
                    ></input>
                </div>
                <div className="flex flex-col gap-3">
                    <label htmlFor="lname" className="text-16 md:text-18">
                        Last Name
                    </label>
                    <input
                        id="lname"
                        className="rounded-sm border border-gray-disabled px-1 min-h-10"
                        value={lastNameValue}
                        onChange={(e) => setLastNameValue(e.target.value)}
                    ></input>
                </div>
                <div className="flex flex-col gap-3">
                    <label htmlFor={`phone_${id}`} className="text-16 md:text-18">
                        Phone Number
                    </label>
                    <input
                        id={`phone_${id}`}
                        type="tel"
                        pattern="[0-9]{3}-[0-9]{3}-[0-9]{4}"
                        onKeyUp={handlePhoneChange}
                        aria-invalid={!isPhoneValid}
                        className={classNames(
                            'rounded-sm border px-1 min-h-10',
                            isPhoneValid ? 'border-gray-disabled' : 'border-red'
                        )}
                    ></input>
                    <p
                        className={classNames(isPhoneValid && 'hidden', 'text-red text-14')}
                        aria-hidden={isPhoneValid}
                    >
                        Please enter a valid U.S. phone number.
                    </p>
                </div>
                <div className="flex flex-col gap-3">
                    <label htmlFor={`email_${id}`} className="text-16 md:text-18">
                        Email Address
                    </label>
                    <input
                        id={`email_${id}`}
                        onChange={handleEmailChange}
                        aria-invalid={!isEmailValid}
                        className={classNames(
                            'rounded-sm border px-1 min-h-10',
                            isEmailValid ? 'border-gray-disabled' : 'border-red'
                        )}
                    ></input>
                    <p
                        className={classNames(isEmailValid && 'hidden', 'text-red text-14')}
                        aria-hidden={isZipValid}
                    >
                        Please enter a valid email address.
                    </p>
                </div>
                <div className="flex justify-between">
                    <div className="w-[48%] flex flex-col gap-3">
                        <label htmlFor={`zip_${id}`} className="text-16 md:text-18">
                            ZIP Code
                        </label>
                        <input
                            id={`zip_${id}`}
                            maxLength={5}
                            onChange={handleZipChange}
                            aria-invalid={!isZipValid}
                            value={zipValue}
                            className={classNames(
                                'rounded-sm border px-1 min-h-10',
                                isZipValid ? 'border-gray-disabled' : 'border-red'
                            )}
                        ></input>
                        <p
                            className={classNames(isZipValid && 'hidden', 'text-red text-14')}
                            aria-hidden={isZipValid}
                        >
                            Please enter a valid ZIP code.
                        </p>
                    </div>
                    <div
                        className={classNames(
                            !countyList.length && 'hidden',
                            'w-[48%] flex flex-col gap-3'
                        )}
                    >
                        <label htmlFor={`zip_${id}`} className="text-16 md:text-18">
                            County
                        </label>
                        <select
                            id={`county_${id}`}
                            onChange={(e) => dispatch(updateZipAndCounty(zipValue, e.target.value))}
                            aria-invalid={!!countyList.length && !countyName}
                            value={countyName}
                            className={classNames(
                                'rounded-sm border px-1 min-h-10',
                                !!countyList.length && !countyName
                                    ? 'border-red'
                                    : 'border-gray-disabled'
                            )}
                        >
                            {countyList.map((countyName) => {
                                return (
                                    <option key={countyName} value={countyName}>
                                        {countyName}
                                    </option>
                                );
                            })}
                        </select>
                        <p
                            className={classNames(isZipValid && 'hidden', 'text-red text-14')}
                            aria-hidden={isZipValid}
                        >
                            Please select your county.
                        </p>
                    </div>
                </div>

                <div className={classNames('flex flex-col gap-3', !emailValue && 'opacity-50')}>
                    <p className="text-16 md:text-18">Please select one:</p>
                    <div className="flex gap-1">
                        <input
                            type="radio"
                            id="email_Y"
                            name="email_consent"
                            value="Y"
                            checked={consentValue === 'Y'}
                            onChange={() => setConsentValue('Y')}
                            className="opacity-0"
                            disabled={!emailValue}
                        />
                        <label
                            htmlFor="email_Y"
                            className={classNames(
                                'text-[15px] md:text-18 md:whitespace-nowrap flex items-start',
                                styles.label,
                                consentValue === 'Y' && styles.checked
                            )}
                        >
                            I would like to receive email reminders {'and '}
                            <br className="hidden md:inline" />
                            updates about Medicare from eHealth
                        </label>
                    </div>
                    <div className="flex gap-1">
                        <input
                            type="radio"
                            id="email_N"
                            name="email_consent"
                            value="N"
                            checked={consentValue === 'N'}
                            onChange={() => setConsentValue('N')}
                            className="opacity-0"
                            disabled={!emailValue}
                        />
                        <label
                            htmlFor="email_N"
                            className={classNames(
                                'text-[15px] md:text-18 whitespace-nowrap flex items-start',
                                styles.label,
                                consentValue === 'N' && styles.checked
                            )}
                        >
                            I do not wish to receive emails
                        </label>
                    </div>
                </div>
            </form>

            <button
                onClick={() => void handleSubmit()}
                className={classNames(
                    'w-full max-w-[360px]',
                    'px-3 py-2 mt-4 rounded-md',
                    'font-bold text-18',
                    `bg-branded-${partnername} text-white`
                )}
            >
                Submit Form
            </button>

            <p className="mt-7 text-center text-14 leading-18 lg:leading-7 font-bold max-w-[360px] md:max-w-max">
                Upon submit, this page will redirect to show available plans in your area.
            </p>
            <p className="bg-surface-100 w-full py-4 px-7 text-left text-[15px] text-disclaimer">
                Your information and use of this site is governed by our most recent&nbsp;
                <a
                    href={`${process.env.GATSBY_SITE_URL}/about-ehealth/terms-of-use/?allid=${allid}`}
                    className={`text-branded-${partnername}`}
                    target="_blank"
                    rel="noreferrer"
                    data-wa-link="terms of use"
                >
                    Terms of Use
                </a>
                {' and '}
                <a
                    href={`${process.env.GATSBY_SITE_URL}/about-ehealth/privacy-policy/?allid=${allid}`}
                    className={`text-branded-${partnername}`}
                    target="_blank"
                    rel="noreferrer"
                    data-wa-link="privacy policy"
                >
                    Privacy Policy
                </a>
                . By giving your information above and clicking the Submit Form button, you consent
                to receive calls, emails or text messages marketing Medicare Advantage, Medicare
                supplement insurance and prescription drug plan options (at any number or email
                address you provide) from eHealth or a licensed insurance agent partner using an
                autodialing system or artificial/prerecorded voice even if on a government
                do-not-call registry. Standard message and data rates apply. This agreement is not a
                condition of enrollment.
            </p>
        </div>
    );
};

export default PartnerZipLeadComboForm;
